import { BRAND, BRAND_LONG } from "@/lib/brand";
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, CalendarDays, Clock, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  pending_payment: { text: "Awaiting payment", color: "text-amber-700 bg-amber-50 border-amber-200" },
  payment_uploaded: { text: "Payment received — awaiting staff verification", color: "text-blue-700 bg-blue-50 border-blue-200" },
  confirmed: { text: "Confirmed — slot reserved", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  cancelled: { text: "Cancelled", color: "text-red-700 bg-red-50 border-red-200" },
  arrived: { text: "You’ve arrived — see the queue", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  no_show: { text: "Marked as no-show", color: "text-slate-700 bg-slate-100 border-slate-200" },
  done: { text: "Completed", color: "text-slate-700 bg-slate-100 border-slate-200" },
};

const LOCKED = new Set(["arrived", "done", "no_show", "completed", "cancelled"]);
const CUTOFF_HOURS = 24;

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function BookingConfirmed() {
  const { slug, rid } = useParams();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("view"); // view | reschedule
  const [busy, setBusy] = useState(false);

  // Reschedule picker state
  const [date, setDate] = useState(todayStr());
  const [slotInfo, setSlotInfo] = useState({ slots: [], active: true });
  const [slot, setSlot] = useState("");

  const load = async () => {
    try {
      const { data: d } = await api.get(`/public/reservations/${rid}`);
      setData(d);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => {
    let stop = false;
    const run = async () => { if (!stop) await load(); };
    run();
    const id = setInterval(() => { if (mode === "view") run(); }, 8000); // pause polling while editing
    return () => { stop = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, mode]);

  const r = data?.reservation || {};

  // Can the patient still self-cancel / reschedule? (outside the 24h cutoff, not locked)
  const canModify = useMemo(() => {
    if (!r.status || LOCKED.has(r.status)) return false;
    if (!r.reservation_date || !r.reservation_time) return false;
    const apptMs = Date.parse(`${r.reservation_date}T${r.reservation_time}:00+08:00`);
    if (Number.isNaN(apptMs)) return true;
    return (apptMs - Date.now()) / 3_600_000 >= CUTOFF_HOURS;
  }, [r.status, r.reservation_date, r.reservation_time]);

  // Load slots for the reschedule picker
  useEffect(() => {
    if (mode !== "reschedule" || !r.doctor_id || !date) return;
    (async () => {
      try {
        const { data: s } = await api.get(`/public/hospitals/${slug}/doctors/${r.doctor_id}/slots`, { params: { date } });
        setSlotInfo(s || { slots: [], active: true });
        setSlot("");
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, date, r.doctor_id, slug]);

  const cancelBooking = async () => {
    if (!window.confirm("Cancel this appointment? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/public/reservations/${rid}/cancel`);
      toast.success("Appointment cancelled");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setBusy(false); }
  };

  const submitReschedule = async () => {
    if (!date || !slot) return toast.error("Pick a new date and time");
    setBusy(true);
    try {
      await api.post(`/public/reservations/${rid}/reschedule`, { reservation_date: date, reservation_time: slot });
      toast.success("Appointment rescheduled");
      setMode("view");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  const status = LABELS[r.status] || { text: r.status, color: "text-slate-700 bg-slate-50 border-slate-200" };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="booking-confirmed">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-3xl shadow-sm p-8 text-center">
        <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
        <h1 className="text-2xl md:text-3xl font-extrabold mt-4 text-slate-900" style={{ fontFamily: "Work Sans" }}>Submitted</h1>
        <div className="text-slate-500 mt-1">{data.hospital_name}</div>

        <div className="mt-6 grid grid-cols-2 gap-3 text-left">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Date</div>
            <div className="font-semibold text-slate-900 mt-0.5">{r.reservation_date}</div>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Time</div>
            <div className="font-semibold text-slate-900 mt-0.5">{r.reservation_time}</div>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-500">Patient</div>
            <div className="font-semibold text-slate-900 mt-0.5">{r.patient_name}</div>
          </div>
        </div>

        <div className={`mt-5 rounded-xl border p-3 text-sm font-medium ${status.color}`} data-testid="reservation-status">
          {status.text}
        </div>

        {/* Reschedule picker */}
        {mode === "reschedule" ? (
          <div className="mt-5 text-left border border-slate-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">Pick a new date & time</div>
              <button onClick={() => setMode("view")} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="relative">
              <CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="h-11 pl-9" data-testid="reschedule-date" />
            </div>
            {!slotInfo.active ? (
              <div className="text-sm text-red-600">Doctor is not available on this date.</div>
            ) : slotInfo.slots.length === 0 ? (
              <div className="text-sm text-slate-500">No open slots for this date.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-auto pr-1">
                {slotInfo.slots.map((s) => (
                  <button key={s} type="button" onClick={() => setSlot(s)}
                    className={`h-10 rounded-lg border text-sm font-medium ${slot === s ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 hover:border-blue-400 text-slate-700"}`}
                    data-testid={`reschedule-slot-${s}`}>
                    <Clock size={12} className="inline mr-1 -mt-0.5" />{s}
                  </button>
                ))}
              </div>
            )}
            <Button onClick={submitReschedule} disabled={busy || !slot} className="w-full h-11 bg-blue-700 hover:bg-blue-800" data-testid="reschedule-submit">
              {busy ? "Saving…" : "Confirm new time"}
            </Button>
          </div>
        ) : canModify ? (
          <div className="mt-5 flex gap-3">
            <Button variant="outline" onClick={() => { setDate(r.reservation_date || todayStr()); setMode("reschedule"); }} disabled={busy} className="flex-1 h-11" data-testid="btn-reschedule">
              <RefreshCw size={15} className="mr-1" />Reschedule
            </Button>
            <Button variant="outline" onClick={cancelBooking} disabled={busy} className="flex-1 h-11 text-red-600 border-red-200 hover:bg-red-50" data-testid="btn-cancel">
              <X size={15} className="mr-1" />Cancel
            </Button>
          </div>
        ) : r.status === "cancelled" ? (
          <div className="mt-5">
            <Link to={`/book/${slug}`} className="inline-block w-full">
              <Button className="w-full h-11 bg-blue-700 hover:bg-blue-800" data-testid="btn-rebook">Book a new appointment</Button>
            </Link>
          </div>
        ) : (
          <p className="mt-4 text-xs text-slate-500">
            To cancel or reschedule within {CUTOFF_HOURS} hours of your appointment, please contact the clinic directly.
          </p>
        )}

        <p className="mt-4 text-xs text-slate-500">Your reservation ID is <span className="font-mono">{rid}</span>. Save this page; we’ll update it automatically.</p>

        <Link to="/login" className="inline-block mt-6 text-sm text-blue-700 font-medium hover:underline">Back to {BRAND}</Link>
      </div>
    </div>
  );
}
